/**
 * Locale-aware display names for ISO codes.
 *
 * The DB stores `nationalities`, `country_of_residence`, and
 * `languages` as ISO codes (`US`, `IT`, `en`, `fr`). Render sites
 * should never display the raw codes — they're keys, not labels. This
 * helper wraps `Intl.DisplayNames` (built into Node + every modern
 * browser, no extra dependency) so callers can ask for "the country
 * `US` in French" and get back "États-Unis".
 *
 * Why a wrapper rather than calling `Intl.DisplayNames` directly:
 *  1. Memoise the constructor — instantiating one per call is wasteful
 *     when the same locale + type are used dozens of times per page.
 *  2. Defensive fallback — `Intl.DisplayNames` throws on unsupported
 *     locales and returns `undefined` for unknown codes; we want both
 *     paths to fall back to the raw code so a missing translation
 *     never blanks a cell.
 *  3. Centralise the locale-resolution rule — country names use the
 *     `'region'` type, languages use `'language'`, dropdown labels for
 *     currency/script use other types. Naming the call sites avoids
 *     the wrong type leaking in.
 *
 * Server components can pass the resolved page locale; client
 * components can use `useTranslation().i18n.language`. Default falls
 * back to `'en'` so the function is safe to call before locale is
 * known.
 */

interface DisplayNamesCacheKey {
  locale: string;
  type: Intl.DisplayNamesType;
}

const cache = new Map<string, Intl.DisplayNames>();

function get(key: DisplayNamesCacheKey): Intl.DisplayNames | null {
  const cacheKey = `${key.locale}::${key.type}`;
  let cached = cache.get(cacheKey);
  if (cached) return cached;
  try {
    cached = new Intl.DisplayNames([key.locale], { type: key.type });
    cache.set(cacheKey, cached);
    return cached;
  } catch {
    // Locale not supported by the runtime — fall back to English so
    // we still get a translated name rather than the raw code.
    if (key.locale !== 'en') return get({ ...key, locale: 'en' });
    return null;
  }
}

/**
 * Country name for an ISO 3166-1 alpha-2 code in the given locale.
 * Falls back to the raw code if the runtime can't resolve it.
 *
 * Examples:
 *   countryDisplayName('US', 'en') → 'United States'
 *   countryDisplayName('US', 'fr') → 'États-Unis'
 *   countryDisplayName('CH', 'it') → 'Svizzera'
 */
export function countryDisplayName(
  code: string | null | undefined,
  locale: string = 'en',
): string {
  if (!code) return '';
  const trimmed = code.trim();
  if (!trimmed) return '';
  const dn = get({ locale, type: 'region' });
  if (!dn) return trimmed;
  try {
    return dn.of(trimmed.toUpperCase()) ?? trimmed;
  } catch {
    return trimmed;
  }
}

/**
 * Language name for an ISO 639-1 code in the given locale. Falls back
 * to the raw code if the runtime can't resolve it.
 *
 * Examples:
 *   languageDisplayName('en', 'en') → 'English'
 *   languageDisplayName('fr', 'en') → 'French'
 *   languageDisplayName('en', 'fr') → 'anglais'
 */
export function languageDisplayName(
  code: string | null | undefined,
  locale: string = 'en',
): string {
  if (!code) return '';
  const trimmed = code.trim();
  if (!trimmed) return '';
  const dn = get({ locale, type: 'language' });
  if (!dn) return trimmed;
  try {
    return dn.of(trimmed.toLowerCase()) ?? trimmed;
  } catch {
    return trimmed;
  }
}
